import { Column, Lookup } from 'devextreme-react/data-grid';
import * as ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { getAllWarehouses, getZoneByWarehouseId } from '../../../services/apis';
import { ButtonOnExporting } from '../../../utils/DataGridExporter';
import useFormatMessage from '../../../utils/useFormatMessage';
import SayimPopup from './SayimPopup';
const InventoryCountList = () => {
    const { t } = useFormatMessage();
    const [dataSources, setDataSources] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [zones, setZones] = useState([]);
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [isZonesLoaded, setIsZonesLoaded] = useState(false);
    const [downloadingReports, setDownloadingReports] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { hasRole } = useAuth(30);

    const [formData, setFormData] = useState({
        count_code: '',
        warehouse_id: null,
        zone_id: [],
        is_address_based: false,
        start_date: '',
        status: 'CREATED',
        id: null
    });
    const ref = useRef();
    const mountedRef = useRef(true);

    useEffect(() => {
        fetchWarehouses();
        fetchData();
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchData = async () => {
        try {
            ref.current.instance.beginCustomLoading();
            const response = await api.post("/getAllInventoryCounts");
            if (mountedRef.current) {
                setDataSources(response.data || []);
            }
        } catch (error) {
            if (mountedRef.current) {
                toast.error(t("Error occurred"));
                console.error("Error fetching inventory counts:", error);
            }
        } finally {
            if (ref.current && ref.current.instance) {
                ref.current.instance.endCustomLoading();
            }
        }
    };

    const fetchWarehouses = async () => {
        try {
            const response = await getAllWarehouses();
            if (mountedRef.current) {
                setWarehouses(response);
            }
        } catch (error) {
            if (mountedRef.current) {
                toast.error(t("Failed to fetch warehouse information"));
            }
        }
    };

    const fetchZoneByWareHouseID = async (warehouseId) => {
        if (!mountedRef.current) return;

        setIsZonesLoaded(false);
        try {
            const response = await getZoneByWarehouseId(warehouseId);
            if (mountedRef.current) {
                setZones(Array.isArray(response) ? response : []);
                setIsZonesLoaded(true);
            }
        } catch (error) {
            if (mountedRef.current) {
                toast.error(t("Failed to fetch zone information"));
                setZones([]);
                setIsZonesLoaded(true);
            }
        }
    };

    const handleWarehouseChange = async (e) => {
        if (!e.value) return;

        // Sadece yeni kayıt oluştururken zone_id'yi sıfırla
        if (formData.id === null) {
            setFormData(prev => ({
                ...prev,
                warehouse_id: e.value,
                zone_id: []
            }));
            await fetchZoneByWareHouseID(e.value);
        } else {
            // Edit işleminde sadece warehouse_id'yi güncelle, zone_id'yi koru
            setFormData(prev => ({
                ...prev,
                warehouse_id: e.value
            }));
            await fetchZoneByWareHouseID(e.value);
        }
    };



    const handleSubmit = async () => {
        if (!mountedRef.current) return;

        // Çift tıklama koruması
        if (isSubmitting) {
            return;
        }

        // Form validasyonu
        if (!formData.warehouse_id) {
            toast.error(t("Depo seçimi zorunludur"));
            return;
        }

        if (!formData.zone_id || formData.zone_id.length === 0) {
            toast.error(t("Bölge seçimi zorunludur"));
            return;
        }

        if (!formData.start_date) {
            toast.error(t("Başlangıç tarihi zorunludur"));
            return;
        }

        if (!formData.status) {
            toast.error(t("Durum seçimi zorunludur"));
            return;
        }

        // STARTED durumundaki kayıt tekrar STARTED olarak güncellenmeye çalışılıyorsa hata ver
        if (formData.id !== null && formData.status === "STARTED") {
            const currentRecord = dataSources.find(item => item.id === formData.id);
            if (currentRecord && currentRecord.status === "STARTED") {
                toast.error(t("Bu sayım zaten başlatılmıştır"));
                return;
            }
        }

        // Loading durumunu başlat
        setIsSubmitting(true);

        const BASEURL = formData.id === null ? "/createInventoryCount" : "/updateInventoryCount";
        let body = formData.id === null ? {
            create_user_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii')),
            is_address_based: formData.is_address_based,
            start_date: moment(formData.start_date).format("YYYY-MM-DD"),
            status: formData.status,
            warehouse_id: formData.warehouse_id,
            zone_ids: Array.isArray(formData.zone_id) ? formData.zone_id : [],
            update_user_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
        } : {
            status: formData.status,
            id: formData.id,
            is_address_based: formData.is_address_based,
            start_date: moment(formData.start_date).format("YYYY-MM-DD"),
            update_user_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
        }
        try {
            const response = await api.post(BASEURL, body);
            if (response.status === 200 && mountedRef.current) {
                toast.success(t("Successfully added"));
                fetchData();
                handlePopupClose();
            }
        } catch (error) {
            if (mountedRef.current) {
                const errorMessage = error.response?.data?.error || t("Error occurred");
                toast.error(errorMessage);
            }
        } finally {
            // Loading durumunu bitir
            if (mountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const handlePopupClose = () => {
        if (!mountedRef.current) return;

        setPopupVisible(false);
        setFormData({
            count_code: '',
            warehouse_id: null,
            zone_id: [],
            is_address_based: false,
            start_date: '',
            status: 'CREATED',
            id: null,
        });
        setZones([]);
        setIsZonesLoaded(false);
    };

    const getAllStatusOptions = () => [
        { id: "CREATED", text: "Sayım Girişi Yapıldı" },
        { id: "STARTED", text: "Sayım Başlatıldı" },
        { id: "COMPLETED", text: "Sayım Tamamlandı" },
        { id: "CANCELLED", text: "Sayım İptal Edildi" },
    ];


    return (
        <>
            <Breadcrumb current={t("Sayım Aç Kapat")} />
            <Card className="p-4">
                <div className="d-flex justify-content-end mb-2">
                    <Button
                        color="light"
                        outline
                        onClick={fetchData}
                        className="me-2 mr-1"
                        style={{
                            paddingInline: "10px",
                            border: "1px solid #ddd"
                        }}
                    >
                        <i className="fa fa-refresh"></i>
                    </Button>
                    {
                        hasRole("sayim_ekleme_add") &&
                        <Button
                            outline
                            color='light'
                            style={{
                                border: "1px solid #ddd",
                                paddingInline: "10px",
                            }}
                            className="me-2 mr-1"
                            onClick={() => {
                                setFormData({
                                    count_code: '',
                                    warehouse_id: null,
                                    zone_id: [],
                                    is_address_based: false,
                                    start_date: '',
                                    status: 'CREATED',
                                    id: null
                                })
                                setPopupVisible(true)
                            }}
                        >
                            <i className="fa fa-plus me-2"></i> Ekle

                        </Button>}
                    <Button
                        color="success"
                        style={{
                            paddingInline: "10px",
                        }}
                        onClick={(e) => ButtonOnExporting(e, ref, "Inventory Count List")}
                    >
                        <i className="fa fa-file-excel-o"></i>
                    </Button>
                </div>

                <DatagridCustom
                    dataSource={dataSources}
                    reff={ref}
                >
                    <Column
                        dataField="count_code"
                        caption={t("Sayım Kodu")}
                        alignment="center"
                    />
                    <Column
                        dataField="warehouse_name"
                        caption={t("Depo Adı")}
                        alignment="center"
                    />
                    <Column
                        dataField="zone_name"
                        caption={t("Bölge Adı")}
                        alignment="center"
                        calculateDisplayValue={(e) => {
                            return e.zones.map((item) => item.zone_name).join(", ");
                        }}
                    />
                    <Column
                        dataField="start_date"
                        caption={t("Başlangıç Tarihi")}
                        dataType="date"
                        alignment="center"
                    />
                    <Column
                        dataField="end_date"
                        caption={t("Bitiş Tarihi")}
                        dataType="date"
                        alignment="center"
                    />
                    create_user_name
                    <Column
                        dataField="create_user_name"
                        caption={t("Oluşturan")}
                        alignment="center"
                    />
                    <Column
                        dataField="status"
                        caption={t("Durum")}
                        alignment="center"
                    >
                        <Lookup dataSource={getAllStatusOptions()} valueExpr="id" displayExpr="text" />
                    </Column>
                    <Column
                        dataField="is_address_based"
                        caption={t("Adresli")}
                        alignment="center"
                        width={80}
                    />
                    <Column
                        caption='Rapor'
                        width={128}
                        alignment='center'
                        cellRender={(e) => {
                            // Sadece başlatılmış sayımlar için rapor butonu göster
                            if (e.data.status !== "STARTED" && e.data.status !== "COMPLETED" && e.data.status !== "CANCELLED") {
                                return null;
                            }

                            const isDownloading = downloadingReports[e.data.count_code];

                            return (
                                <button
                                    style={{
                                        cursor: isDownloading ? "not-allowed" : "pointer",
                                        outline: "none",
                                        boxShadow: "none",
                                        borderRadius: "2px",
                                        backgroundColor: 'rgb(74 140 211)',
                                        color: "white",
                                        width: "100px"
                                    }}
                                    disabled={isDownloading}
                                    onClick={async () => {
                                        if (!mountedRef.current || isDownloading) return;

                                        // Loading durumunu başlat
                                        setDownloadingReports(prev => ({
                                            ...prev,
                                            [e.data.count_code]: true
                                        }));

                                        try {
                                            const response = await api.post("/reports/getInventoryCountResultExcel", {
                                                count_code: e.data.count_code
                                            });

                                            if (response.data && response.data && response.data) {
                                                // Veriyi hazırla
                                                const preparedData = response.data.map(item => {
                                                    let resultText = '';
                                                    if (item.result === 'doğru') {
                                                        resultText = 'Doğru';
                                                    } else if (item.result === 'sayılmayan') {
                                                        resultText = 'Sayılmayan';
                                                    } else if (item.result === 'sayım fazlası') {
                                                        resultText = 'Sayım Fazlası';
                                                    } else if (item.result === 'farklı adresde sayılmış') {
                                                        resultText = 'Farklı Adresde Sayılmış';
                                                    } else {
                                                        resultText = item.result;
                                                    }

                                                    return {
                                                        'Şase': item.chassis_no || '',
                                                        'Model': item.model || '',
                                                        'Renk': item.color || '',
                                                        'Park Adres': item.old_park || '',
                                                        'Sayım Adres': item.counting_park || '',
                                                        'Sonuç': resultText
                                                    };
                                                });

                                                const workbook = new ExcelJS.Workbook();
                                                const worksheet = workbook.addWorksheet('Sayım Raporu');

                                                // Başlık satırını ekle
                                                const headers = Object.keys(preparedData[0]);
                                                const headerRow = worksheet.addRow(headers);

                                                // Header satırını mavi yap
                                                headerRow.eachCell((cell) => {
                                                    cell.fill = {
                                                        type: 'pattern',
                                                        pattern: 'solid',
                                                        fgColor: { argb: 'FF4472C4' } // Mavi renk
                                                    };
                                                    cell.font = {
                                                        bold: true,
                                                        color: { argb: 'FFFFFFFF' } // Beyaz yazı
                                                    };
                                                    // Border kaldırıldı
                                                });

                                                // Sütun özelliklerini ayarla
                                                worksheet.columns = headers.map((header) => ({
                                                    header,
                                                    key: header,
                                                    width: Math.min(Math.max(header.length + 2, 15), 30)
                                                }));

                                                // Veri satırlarını ekle
                                                preparedData.forEach((row) => {
                                                    const dataRow = worksheet.addRow(Object.values(row));
                                                    // Veri satırlarına border kaldırıldı
                                                });

                                                // Dosya adı için güvenli isim oluştur
                                                const safeFileName = `sayim_raporu_${e.data.count_code}`;

                                                // Koşullu biçimlendirme ekle - Sonuç kolonuna göre satır renklendirmesi
                                                const resultColumnIndex = headers.indexOf('Sonuç') + 1; // Excel'de 1-based index (F=6)
                                                const dataRange = `A2:F${worksheet.rowCount}`;

                                                // Her bir koşul için ayrı ayrı conditional formatting ekle
                                                const conditions = [
                                                    { text: 'Doğru', color: 'FF9FD77F' }, // Yeşil
                                                    { text: 'Sayılmayan', color: 'FFEE7E6C' }, // Kırmızı  
                                                    { text: 'Farklı Adresde Sayılmış', color: 'FFFF54' }, // Sarı
                                                    { text: 'Sayım Fazlası', color: 'FFB3D9FF' } // Canlı mavi
                                                ];

                                                conditions.forEach(condition => {
                                                    worksheet.addConditionalFormatting({
                                                        ref: dataRange,
                                                        rules: [{
                                                            type: 'expression',
                                                            formulae: [`$F2="${condition.text}"`],
                                                            style: {
                                                                fill: {
                                                                    type: 'pattern',
                                                                    pattern: 'solid',
                                                                    fgColor: { argb: condition.color }
                                                                }
                                                            }
                                                        }]
                                                    });
                                                });
                                                worksheet.eachRow((row, rowNumber) => {
                                                    if (rowNumber > 1) { // Header satırını atla
                                                        const resultCell = row.getCell(resultColumnIndex);
                                                        const resultValue = resultCell.value;

                                                        let fillColor = null;
                                                        switch (resultValue) {
                                                            case 'Doğru':
                                                                fillColor = 'FF9FD77F';
                                                                break;
                                                            case 'Sayılmayan':
                                                                fillColor = 'FFEE7E6C';
                                                                break;
                                                            case 'Farklı Adresde Sayılmış':
                                                                fillColor = 'FFFF54';
                                                                break;
                                                            case 'Sayım Fazlası':
                                                                fillColor = 'FFB3D9FF';
                                                                break;
                                                        }

                                                        if (fillColor) {
                                                            // Tüm satırı renklendir
                                                            for (let col = 1; col <= headers.length; col++) {
                                                                row.getCell(col).fill = {
                                                                    type: 'pattern',
                                                                    pattern: 'solid',
                                                                    fgColor: { argb: fillColor }
                                                                };
                                                            }
                                                        }
                                                    }
                                                });
                                                const buffer = await workbook.xlsx.writeBuffer();
                                                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${safeFileName}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);

                                                toast.success(t("Rapor başarıyla indirildi"));
                                            } else {
                                                toast.error(t("Rapor verisi bulunamadı"));
                                            }
                                        } catch (error) {
                                            if (mountedRef.current) {
                                                toast.error(t("Rapor indirme hatası"));
                                                console.error("Rapor indirme hatası:", error);
                                            }
                                        } finally {
                                            // Loading durumunu bitir
                                            if (mountedRef.current) {
                                                setDownloadingReports(prev => ({
                                                    ...prev,
                                                    [e.data.count_code]: false
                                                }));
                                            }
                                        }
                                    }}
                                >
                                    {isDownloading ? (
                                        <i className="fa fa-spinner fa-spin"></i>
                                    ) : (
                                        'İNDİR'
                                    )}
                                </button>
                            )
                        }}
                    />
                    <Column caption='İşlem' alignment='center'
                        visible={hasRole("sayim_ekleme_update")}
                        cellRender={(e) => {
                            // Tamamlanan veya iptal edilen sayımlar için edit butonu gösterilmez
                            if (e.data.status === "COMPLETED" || e.data.status === "CANCELLED") {
                                return null;
                            }

                            return (
                                <i className="fa fa-edit"
                                    style={{
                                        cursor: "pointer",
                                        color: "blue",
                                        fontSize: "1.4em",

                                    }}
                                    onClick={async () => {
                                        if (!mountedRef.current) return;

                                        const zoneIds = e.data.zones && Array.isArray(e.data.zones)
                                            ? e.data.zones.map((item) => item.zone_id).filter(zone_id => zone_id != null)
                                            : [];

                                        // Önce zone'ları yükle
                                        if (e.data.warehouse_id) {
                                            await fetchZoneByWareHouseID(e.data.warehouse_id);
                                        }

                                        // FormData'yı set et
                                        await setFormData({
                                            count_code: e.data.count_code,
                                            warehouse_id: e.data.warehouse_id,
                                            zone_id: zoneIds,
                                            is_address_based: e.data.is_address_based,
                                            start_date: moment(e.data.start_date).format("YYYY-MM-DD"),
                                            status: e.data.status,
                                            id: e.data.id
                                        });

                                        // Modal'ı aç
                                        setPopupVisible(true);
                                    }}></i>


                            )
                        }}
                    />

                </DatagridCustom>

                <SayimPopup
                    visible={isPopupVisible}
                    onHiding={handlePopupClose}
                    formData={formData}
                    setFormData={setFormData}
                    warehouses={warehouses}
                    zones={zones}
                    isZonesLoaded={isZonesLoaded}
                    handleWarehouseChange={handleWarehouseChange}
                    handleSubmit={handleSubmit}
                    mountedRef={mountedRef}
                    isSubmitting={isSubmitting}
                />
            </Card>
        </>
    );
};

export default InventoryCountList;